<template>
  <main class="login-page">
    <section class="login-shell">
      <div class="login-hero">
        <div class="login-brand">
          <div class="login-brand-mark">X</div>
          <div>
            <strong>CERTIFICADOR</strong>
            <span>XSolution Tecnologia</span>
          </div>
        </div>

        <div class="login-copy">
          <span>Plataforma autenticada</span>
          <h1>Emissão segura de certificados digitais.</h1>
          <p>
            Acesse o ambiente do Certificador para emitir, rastrear e validar
            documentos com autenticação, QR Code e histórico por empresa.
          </p>
        </div>

        <div class="login-features">
          <div>
            <strong>01</strong>
            <span>Registro oficial por certificado</span>
          </div>
          <div>
            <strong>02</strong>
            <span>QR Code e validação pública</span>
          </div>
          <div>
            <strong>03</strong>
            <span>Preparado para assinatura digital</span>
          </div>
        </div>
      </div>

      <form class="login-card" @submit.prevent="handleLogin">
        <div class="login-card-header">
          <span>Acesso seguro</span>
          <h2>Entrar no Certificador</h2>
          <p>Use suas credenciais para acessar o ambiente de emissão.</p>
        </div>

        <div v-if="errorMessage" class="login-error">
          {{ errorMessage }}
        </div>

        <label>
          <span>E-mail</span>
          <input
            v-model.trim="form.email"
            type="email"
            autocomplete="email"
            placeholder="admin@xsolution.com.br"
            required
          />
        </label>

        <label>
          <span>Senha</span>
          <input
            v-model="form.password"
            type="password"
            autocomplete="current-password"
            placeholder="Digite sua senha"
            required
          />
        </label>

        <button type="submit" :disabled="loading">
          {{ loading ? "Validando acesso..." : "Acessar plataforma" }}
        </button>

        <small>
          Ambiente protegido por sessão segura. O acesso será vinculado à
          empresa do usuário autenticado.
        </small>
      </form>
    </section>
  </main>
</template>

<script setup>
import { reactive, ref } from "vue";
import { useRoute, useRouter } from "vue-router";
import { login } from "@/services/authService";

const router = useRouter();
const route = useRoute();

const loading = ref(false);
const errorMessage = ref("");

const form = reactive({
  email: "",
  password: "",
});

async function handleLogin() {
  loading.value = true;
  errorMessage.value = "";

  try {
    await login({
      email: form.email,
      password: form.password,
    });

    router.push(route.query.redirect || "/");
  } catch (error) {
    errorMessage.value = error.message || "Não foi possível realizar o login.";
  } finally {
    loading.value = false;
  }
}
</script>

<style scoped>
.login-page {
  min-height: 100vh;
  display: grid;
  place-items: center;
  padding: clamp(1rem, 2vw, 2rem);
  background: radial-gradient(
      circle at top left,
      rgba(255, 122, 0, 0.18),
      transparent 30rem
    ),
    radial-gradient(
      circle at bottom right,
      rgba(15, 23, 42, 0.12),
      transparent 34rem
    ),
    linear-gradient(180deg, #f8fafc 0%, #e8edf5 100%);
}

.login-shell {
  width: min(1120px, 100%);
  min-height: min(760px, calc(100vh - 3rem));
  display: grid;
  grid-template-columns: 1.02fr 0.98fr;
  overflow: hidden;
  border: 1px solid rgba(214, 222, 234, 0.95);
  border-radius: 32px;
  background: rgba(255, 255, 255, 0.78);
  box-shadow: 0 30px 90px rgba(15, 23, 42, 0.16);
  backdrop-filter: blur(18px);
}

.login-hero {
  position: relative;
  display: flex;
  flex-direction: column;
  padding: clamp(1.75rem, 3vw, 2.6rem);
  color: #f8fafc;
  background: linear-gradient(
      135deg,
      rgba(5, 7, 13, 0.98),
      rgba(15, 23, 42, 0.96)
    ),
    radial-gradient(
      circle at top right,
      rgba(255, 122, 0, 0.26),
      transparent 22rem
    );
}

.login-hero::after {
  content: "";
  position: absolute;
  right: -90px;
  bottom: -90px;
  width: 280px;
  height: 280px;
  border-radius: 999px;
  background: rgba(255, 122, 0, 0.2);
  filter: blur(4px);
}

.login-brand {
  position: relative;
  z-index: 1;
  display: flex;
  align-items: center;
  gap: 0.85rem;
}

.login-brand-mark {
  display: grid;
  place-items: center;
  width: 48px;
  height: 48px;
  border-radius: 16px;
  color: #111827;
  font-family: "XoloniumBold", sans-serif;
  font-size: 1.15rem;
  background: linear-gradient(135deg, #ffb020, #ff7a00);
  box-shadow: 0 18px 36px rgba(255, 122, 0, 0.28);
}

.login-brand strong,
.login-brand span {
  display: block;
}

.login-brand strong {
  font-family: "XoloniumBold", sans-serif;
  font-size: 1rem;
  letter-spacing: 0.05em;
}

.login-brand span {
  margin-top: 0.25rem;
  color: #94a3b8;
  font-size: 0.74rem;
}

.login-copy {
  position: relative;
  z-index: 1;
  margin-top: 10px;
  margin-bottom: clamp(2rem, 4vw, 3.8rem);
}

.login-copy span {
  display: inline-flex;
  padding: 0.45rem 0.78rem;
  border-radius: 999px;
  color: #fbbf24;
  font-family: "XoloniumBold", sans-serif;
  font-size: 0.66rem;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  background: rgba(251, 191, 36, 0.12);
}

.login-copy h1 {
  max-width: 600px;
  margin: 1.25rem 0 0;
  font-family: "XoloniumBold", sans-serif;
  font-size: clamp(2.65rem, 4.9vw, 0.15rem);
  line-height: 1.02;
  letter-spacing: -0.065em;
}

.login-copy p {
  max-width: 560px;
  margin: 1.1rem 0 0;
  color: #cbd5e1;
  font-size: 0.95rem;
  line-height: 1.75;
}

.login-features {
  position: relative;
  z-index: 1;
  display: grid;
  gap: 0.75rem;
  margin-top: auto;
}

.login-features div {
  display: flex;
  align-items: center;
  gap: 0.85rem;
  min-height: 58px;
  border: 1px solid rgba(148, 163, 184, 0.18);
  border-radius: 18px;
  padding: 0.8rem 1rem;
  background: rgba(15, 23, 42, 0.7);
  box-shadow: 0 12px 28px rgba(0, 0, 0, 0.14);
}

.login-features strong {
  min-width: 34px;
  color: #ffb020;
  font-family: "XoloniumBold", sans-serif;
  font-size: 1rem;
}

.login-features span {
  color: #dbe3ef;
  font-size: 0.8rem;
  line-height: 1.4;
}

.login-card {
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 1rem;
  padding: clamp(2rem, 4vw, 3.6rem);
  background: linear-gradient(
      135deg,
      rgba(255, 255, 255, 0.96),
      rgba(248, 250, 252, 0.84)
    ),
    radial-gradient(
      circle at bottom left,
      rgba(255, 122, 0, 0.13),
      transparent 18rem
    );
}

.login-card-header span {
  display: block;
  color: #f97316;
  font-family: "XoloniumBold", sans-serif;
  font-size: 0.68rem;
  letter-spacing: 0.09em;
  text-transform: uppercase;
}

.login-card-header h2 {
  margin: 0.55rem 0 0;
  color: #111827;
  font-family: "XoloniumBold", sans-serif;
  font-size: clamp(1.75rem, 3vw, 2.35rem);
  line-height: 1.12;
  letter-spacing: -0.045em;
}

.login-card-header p {
  margin: 0.75rem 0 0;
  color: #64748b;
  font-size: 0.95rem;
  line-height: 1.65;
}

.login-card label {
  display: grid;
  gap: 0.45rem;
}

.login-card label span {
  color: #273244;
  font-family: "XoloniumBold", sans-serif;
  font-size: 0.78rem;
}

.login-card input {
  width: 100%;
  border: 1px solid #d6deea;
  border-radius: 16px;
  padding: 0.95rem 1rem;
  color: #111827;
  font-size: 0.92rem;
  background: #ffffff;
  box-shadow: 0 12px 26px rgba(15, 23, 42, 0.05);
  outline: none;
  transition: 0.2s ease;
}

.login-card input::placeholder {
  color: #94a3b8;
}

.login-card input:focus {
  border-color: rgba(255, 122, 0, 0.78);
  background: #fffaf3;
  box-shadow: 0 0 0 4px rgba(255, 122, 0, 0.12),
    0 14px 30px rgba(15, 23, 42, 0.08);
}

.login-card button {
  margin-top: 0.4rem;
  border: 0;
  border-radius: 18px;
  padding: 1rem 1.2rem;
  color: #111827;
  font-family: "XoloniumBold", sans-serif;
  font-size: 0.95rem;
  cursor: pointer;
  background: linear-gradient(135deg, #ffb020, #ff7a00);
  box-shadow: 0 18px 34px rgba(255, 122, 0, 0.22);
  transition: 0.2s ease;
}

.login-card button:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 22px 42px rgba(255, 122, 0, 0.28);
}

.login-card button:disabled {
  cursor: not-allowed;
  opacity: 0.7;
}

.login-card small {
  color: #64748b;
  font-size: 0.82rem;
  line-height: 1.55;
}

.login-error {
  border: 1px solid rgba(220, 38, 38, 0.18);
  border-radius: 16px;
  padding: 0.85rem;
  color: #991b1b;
  background: #fef2f2;
  font-size: 0.82rem;
  line-height: 1.5;
}

@media (max-width: 1180px) {
  .login-shell {
    width: min(1040px, 100%);
  }

  .login-copy h1 {
    font-size: clamp(2.45rem, 4.6vw, 3.55rem);
  }
}

@media (max-width: 900px) {
  .login-page {
    padding: 1rem;
    place-items: start center;
  }

  .login-shell {
    min-height: auto;
    grid-template-columns: 1fr;
    border-radius: 26px;
  }

  .login-hero {
    min-height: auto;
  }

  .login-copy {
    margin-top: 3.5rem;
    margin-bottom: 2.5rem;
  }

  .login-copy h1 {
    max-width: 100%;
    font-size: clamp(2.2rem, 9vw, 3.3rem);
  }

  .login-copy p {
    max-width: 100%;
  }

  .login-card {
    padding: 1.5rem;
  }
}

@media (max-width: 520px) {
  .login-page {
    padding: 0.75rem;
  }

  .login-shell {
    border-radius: 22px;
  }

  .login-hero {
    padding: 1.25rem;
  }

  .login-brand-mark {
    width: 42px;
    height: 42px;
    border-radius: 14px;
  }

  .login-copy {
    margin-top: 2.75rem;
    margin-bottom: 2rem;
  }

  .login-copy h1 {
    font-size: clamp(2rem, 11vw, 2.8rem);
    letter-spacing: -0.055em;
  }

  .login-features div {
    align-items: flex-start;
    min-height: auto;
  }

  .login-card {
    padding: 1.15rem;
  }

  .login-card-header h2 {
    font-size: 1.65rem;
  }
}
</style>
